import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ClipLoader } from "react-spinners";

import { AppointmentDetails } from "../../components/AppointmentDetails/AppointmentDetails";
import { BackButtonHeader } from "../../components/BackButtonHeader/BackButtonHeader";
import { CardWrapper } from "../../components/CardWrapper/CardWrapper";
import { CustomButton } from "../../components/CustomButton/CustomButton";
import { FloatingButtons } from "../../components/FloatingButtons/FloatingButtons";
import { LoadingSpinner } from "../../components/LoadingSpinner/LoadingSpinner";
import Modal from "../../components/Modal/Modal";
import { MonitorTypeListItem } from "../../components/MonitorTypeListItem/MonitorTypeListItem";
import statuses from "../../data/statuses.json";
import { useHandleService } from "../../hooks/useHandleService";
import { useLastServices } from "../../hooks/useLastServices";
import { useService } from "../../hooks/useService";
import { WrenchIcon } from "../../icons/WrenchIcon/WrenchIcon";
import { AppLayout } from "../../layout/AppLayout/AppLayout";
import { goOneStepBack } from "../../services/NavigationService";
import { RootState } from "../../store";
import { adjustPinActions } from "../../store/adjust-pin-slice";
import { checkIsRunning, clockActions } from "../../store/clock-slice";
import { creatingServiceActions } from "../../store/job-slice";
import { creatingMonitorActions } from "../../store/monitor-slice";
import { servicedMonitorsActions } from "../../store/serviced-monitors";
import { activeTabsActions } from "../../store/tabs-slice";
import styles from "../../styles/monitor-box-status.module.css";
import { MonitorEventType } from "../../types/service";
import { ServiceInfo } from "../Customer/Dashboard/ServiceInfo";

export const MonitorBoxStatus = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const monitor = useSelector((state: RootState) => state.monitor.monitor);
  const user = useSelector((state: RootState) => state.user.currentUser.user);
  const appointment = useSelector((state: RootState) => state.appointment);
  const isRunning = useSelector((state: RootState) =>
    checkIsRunning(state, appointment.id, user!.id)
  );

  const monitorTypes = useSelector(
    (state: RootState) => state.monitor.monitorTypes
  );

  const [isStartModalOpen, setIsModalOpen] = useState(false);
  const [isStopModalOpen, setIsModalStopped] = useState(false);
  const [isChangeMonitorModalOpen, setIsChangeMonitorModalOpen] = useState(
    false
  );
  const [loadingComponent, setLoading] = useState(false);
  const [error, setError] = useState("");

  const { stopService } = useService(appointment);

  const { handleStartService, handleStopService } = useHandleService(
    setIsModalOpen,
    setLoading,
    setIsModalStopped
  );

  const { lastServices, lastServicesIncludes, loading } = useLastServices(
    monitor.id,
    5
  );

  const handleBack = () => {
    goOneStepBack(navigate);
  };

  const handleNavigation = (route: string, eventType: MonitorEventType) => {
    dispatch(creatingServiceActions.setEventType(eventType));
    navigate(`/services/${route}`);
  };

  const returnToDashboard = () => {
    stopService(() => {
      dispatch(creatingMonitorActions.resetState());
      dispatch(creatingMonitorActions.setInProgress(false));
      dispatch(creatingMonitorActions.setPropertyId(""));
      dispatch(clockActions.resetClock());
    });

    dispatch(activeTabsActions.setDashbaordUrl(""));

    // Reset tab
    dispatch(activeTabsActions.setProperyInsightTab("Monitors"));

    dispatch(servicedMonitorsActions.resetState());
    navigate("/");
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleStopModal = () => {
    setIsModalStopped(true);
  };

  const handleCloseModal = () => {
    setIsModalStopped(false);
  };

  const handleAdjustPosition = () => {
    if (monitorTypes.map != null) {
      dispatch(
        adjustPinActions.setMonitorPosition({
          x: monitor.x ?? 1,
          y: monitor.y ?? 1,
        })
      );
      dispatch(adjustPinActions.setPosition(monitor.position ?? ""));
      dispatch(
        adjustPinActions.setPinName(
          monitor?.monitorType.slice(0, 3).toUpperCase() ?? ""
        )
      );
      dispatch(adjustPinActions.setLocation(monitor.location));
      dispatch(
        adjustPinActions.setWirelessId({ wirelessId: monitor.wirelessId })
      );
      if (monitor.wirelessId != null)
        dispatch(adjustPinActions.setIsWireless(true));
      else dispatch(adjustPinActions.setIsWireless(false));

      if(monitor.trapType) dispatch(adjustPinActions.setTrapType(monitor.trapType));
      navigate("/monitor/adjust-position-data");
    } else setError(() => t("PropertyAreaMapMissing"));
  };

  const handleRemoveMonitor = () => {
    navigate("/monitor/remove-data");
  };

  const Title = () => (
    <>
      <div className={styles["monitor-status__title"]}>
        <div>
          <p className={styles["monitor-status__monitortype--name"]}>
            {monitor?.monitorType} {monitor?.position}
          </p>
          <p className={styles["monitor-status__propertyarea--name"]}>
            {monitor?.location}
          </p>
        </div>
        <WrenchIcon
          color="dark"
          onClick={() => setIsChangeMonitorModalOpen(true)}
        />
      </div>
    </>
  );

  return (
    <AppLayout hideHeader>
      <Modal
        isOpen={isChangeMonitorModalOpen}
        message={t("MonitorEdit")}
        confirmText={"AdjustMonitor"}
        onConfirm={handleAdjustPosition}
        onCancel={handleRemoveMonitor}
        cancelText={"RemoveMonitor"}
        backButtonShow={() => setIsChangeMonitorModalOpen(false)}
      />
      <div>
        <BackButtonHeader
          handleBackButtonClicked={handleBack}
          showDocumentation={false}
        />

        <AppointmentDetails />
        {loadingComponent && <LoadingSpinner loading={loadingComponent} />}

        {!loadingComponent && (
          <div className={styles["content-wrapper"]}>
            <div className={styles["content"]}>
              <Title />
              <CardWrapper color="white">
                {statuses.map((status: any, i: number) => (
                  <MonitorTypeListItem
                    key={i}
                    onClick={() =>
                      handleNavigation(status.route, status.eventType)
                    }
                    noTitle
                  >
                    <span>{status.name}</span>
                    <img alt="" src="/img/icon-next.svg" />
                  </MonitorTypeListItem>
                ))}
              </CardWrapper>

              <div className={styles["last-services"]}>
                <p className={styles["last-services__title"]}>
                  Letzte Kundenbesuche
                </p>

                {loading ? (
                  <ClipLoader />
                ) : Array.isArray(lastServices) &&
                  lastServices.length > 0 &&
                  lastServicesIncludes.length > 0 ? (
                  lastServices.map((service: any, index: number) => (
                    <div key={index} onClick={() => {}}>
                      <ServiceInfo
                        key={index}
                        service={service}
                        typeOfAppointment={
                          lastServicesIncludes[index]?.appointment
                            ?.typeOfAppointment ?? ""
                        }
                        hideBorderBottom
                        roundedType
                      />
                    </div>
                  ))
                ) : (
                  <p>{t("NoLastServices")}</p>
                )}
              </div>

              {error && <div className={styles["error-message"]}>{error}</div>}

              <FloatingButtons>
                <CustomButton
                  color={isRunning ? "red" : "green"}
                  icon="clock"
                  iconPosition="left"
                  onClick={isRunning ? handleStopModal : handleOpenModal}
                  width={80}
                >
                  {isRunning ? "Service beenden" : "Service starten"}
                </CustomButton>
              </FloatingButtons>

              {isStartModalOpen && (
                <Modal
                  isOpen={isStartModalOpen}
                  message={t("ServiceStartModalMessage")}
                  note={"ServiceStartModalNote"}
                  onCancel={() => setIsModalOpen(false)}
                  onConfirm={handleStartService}
                />
              )}

              {isStopModalOpen && (
                <Modal
                  isOpen={isStopModalOpen}
                  message={t("ServiceStopModalMessage")}
                  onCancel={returnToDashboard}
                  onConfirm={handleStopService}
                  cancelText={"CancelText"}
                  confirmText={"ConfirmText"}
                  backButtonShow={handleCloseModal}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
};
